if (!customElements.get('mosaic-navigation')) {
  class MosaicNavigation extends HTMLElement {
    #abortController;
    #scrollController;
    #scrollTimer;

    connectedCallback() {
      this.#abortController = new AbortController();
      this.addEventListener('click', this.#handleClick, { signal: this.#abortController.signal });
    }

    disconnectedCallback() {
      this.#abortController?.abort();
      this.#cancelScrollCorrection();
    }

    #cancelScrollCorrection = () => {
      this.#scrollController?.abort();
      clearTimeout(this.#scrollTimer);
    };

    #getTarget(targetId, childTargetId) {
      const templateId = this.dataset.sectionId?.split('__')[0];
      if (!templateId || !targetId) return null;

      const sectionTarget = document.getElementById(`shopify-section-${templateId}__${targetId}`);
      if (!sectionTarget || !childTargetId) return sectionTarget;

      const childTarget = document.getElementById(childTargetId);
      return sectionTarget.contains(childTarget) ? childTarget : sectionTarget;
    }

    #scrollBehavior(targetTop) {
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (prefersReducedMotion) return 'auto';

      const scrollPinEnabled = window.matchMedia('(min-width: 1280px)').matches;
      if (!scrollPinEnabled) return 'smooth';

      const hasPinnedCarouselInPath = Array.from(
        document.querySelectorAll('immersive-feature-carousel[data-scroll-pin="true"]')
      ).some((carousel) => {
        const carouselTop = carousel.getBoundingClientRect().top + window.scrollY;
        return carouselTop > window.scrollY && carouselTop < targetTop;
      });

      return hasPinnedCarouselInPath ? 'auto' : 'smooth';
    }

    #handleClick = (event) => {
      if (!(event.target instanceof Element)) return;

      const control = event.target.closest('[data-navigation-target]');
      if (!control || !this.contains(control)) return;
      this.#cancelScrollCorrection();

      const mobileChildTarget = window.matchMedia('(max-width: 767px)').matches
        ? control.dataset.navigationMobileChildTarget
        : undefined;
      const target = this.#getTarget(
        control.dataset.navigationTarget,
        mobileChildTarget || control.dataset.navigationChildTarget,
      );
      if (!target) return;

      if (control.dataset.navigationPosition === 'tri-fusion-image-complete') {
        const cinematic = target.querySelector('tri-fusion-cinematic');
        if (cinematic?.scrollToImageComplete?.()) return;
      }

      if (control.dataset.navigationPosition === 'tri-fusion-heading-visible') {
        const cinematic = target.querySelector('tri-fusion-cinematic');
        if (cinematic?.scrollToHeadingVisible?.()) return;
      }

      if (control.dataset.navigationPosition === 'pinned-gallery-index') {
        const gallery = target.querySelector('pinned-horizontal-gallery');
        const index = Number.parseInt(control.dataset.navigationGalleryIndex, 10);
        if (!Number.isNaN(index) && gallery?.goToIndex?.(index)) return;
      }

      const top = this.#targetScrollTop(target);
      const behavior = this.#scrollBehavior(top);
      this.#scrollController = new AbortController();
      const signal = this.#scrollController.signal;
      const finish = () => {
        this.#cancelScrollCorrection();
        if (!target.isConnected) return;
        // Earlier scroll-driven sections can change layout during the journey.
        const finalTop = this.#targetScrollTop(target, true);
        if (Math.abs(window.scrollY - finalTop) > 1) {
          window.scrollTo({ top: finalTop, behavior: 'instant' });
        }
      };
      for (const eventName of ['wheel', 'touchstart', 'pointerdown', 'keydown']) {
        window.addEventListener(eventName, this.#cancelScrollCorrection, { passive: true, signal });
      }
      this.#scrollTimer = setTimeout(finish, 2000);
      window.scrollTo({ top, behavior });
    };

    #targetScrollTop(target, settled = false) {
      const stickyNavigation = document.querySelector('.shopify-section__anchor_tab_responsive');
      let offset = stickyNavigation instanceof HTMLElement ? stickyNavigation.offsetHeight : 0;
      let targetTop = 0;

      const styles = getComputedStyle(target);
      const headerHeight = Number.parseFloat(styles.getPropertyValue('--header-height')) || 0;
      const stickyHeader = Number.parseFloat(styles.getPropertyValue('--sticky-header-enabled')) || 0;
      const stickyAnnouncement = Number.parseFloat(styles.getPropertyValue('--sticky-announcement-bar-enabled')) || 0;
      const announcementHeight = Number.parseFloat(styles.getPropertyValue('--announcement-bar-height')) || 0;
      offset += stickyHeader * headerHeight + stickyAnnouncement * announcementHeight;

      // Layout offsets exclude the entrance animation's temporary translation.
      for (let element = target; element; element = element.offsetParent) {
        targetTop += element.offsetTop;
      }
      if (settled) targetTop = target.getBoundingClientRect().top + window.scrollY;

      return Math.max(0, targetTop - offset);
    }
  }

  customElements.define('mosaic-navigation', MosaicNavigation);
}
